import { traduzirErroSupabase } from '@octawash/shared';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase, tenantCodeAtual } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';

export function SignupPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [senhaConfirm, setSenhaConfirm] = useState('');
  const [aceitouTermos, setAceitouTermos] = useState(false);

  const signup = useMutation({
    mutationFn: async () => {
      if (!tenantCodeAtual) throw new Error('Subdominio do tenant não identificado');
      if (senha !== senhaConfirm) throw new Error('Senhas não coincidem');
      if (senha.length < 10) throw new Error('Senha precisa de pelo menos 10 caracteres');
      if (!aceitouTermos) throw new Error('Aceite os termos para continuar');

      const { data, error } = await supabase.auth.signUp({
        email,
        password: senha,
        options: {
          data: { cod_tenant: tenantCodeAtual, full_name: nome },
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Conta criada!', { description: 'Verifique seu e-mail para confirmar.' });
      navigate('/login');
    },
    onError: (e: Error) =>
      toast.error('Falha no cadastro', { description: traduzirErroSupabase(e) }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    signup.mutate();
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-5">
      <div className="bg-sidebar text-sidebar-foreground hidden flex-col justify-between p-12 lg:col-span-2 lg:flex">
        <div className="flex items-center gap-3 text-xl font-semibold">
          {tenant?.des_logo_url ? (
            <img src={tenant.des_logo_url} alt={tenant.nom_tenant} className="h-10 w-10 rounded" />
          ) : (
            <div className="bg-primary text-primary-foreground flex h-10 w-10 items-center justify-center rounded font-bold">
              {(tenant?.nom_tenant ?? 'OW').slice(0, 2).toUpperCase()}
            </div>
          )}
          <span>{tenant?.nom_tenant ?? 'Octa Pontos'}</span>
        </div>
        <div className="space-y-3">
          <p className="text-3xl font-bold leading-tight">Crie sua conta agora.</p>
          <p className="text-sidebar-foreground/70">
            Lavagens ilimitadas, cobrança automática, controle pelo app.
          </p>
        </div>
        <p className="text-sidebar-foreground/50 text-xs">
          © {new Date().getFullYear()} Octa Pontos
        </p>
      </div>

      <div className="bg-background flex flex-col items-center justify-center p-6 sm:p-12 lg:col-span-3">
        <div className="w-full max-w-sm space-y-6">
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold">Criar conta</h1>
            <p className="text-muted-foreground text-sm">
              Cadastro em{' '}
              <strong className="text-foreground">
                {tenant?.nom_tenant ?? tenantCodeAtual ?? 'Octa Pontos'}
              </strong>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome completo</Label>
              <Input id="nome" required value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="senha">Senha (min. 10 caracteres)</Label>
              <Input
                id="senha"
                type="password"
                required
                autoComplete="new-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="senha-c">Confirme a senha</Label>
              <Input
                id="senha-c"
                type="password"
                required
                autoComplete="new-password"
                value={senhaConfirm}
                onChange={(e) => setSenhaConfirm(e.target.value)}
              />
            </div>

            <label className="text-muted-foreground flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={aceitouTermos}
                onChange={(e) => setAceitouTermos(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Li e aceito os{' '}
                <a
                  href={tenant?.des_url_termos ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  termos de uso
                </a>{' '}
                e a{' '}
                <a
                  href={tenant?.des_url_pol_priv ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  politica de privacidade
                </a>
                .
              </span>
            </label>

            <Button type="submit" className="w-full" disabled={signup.isPending}>
              {signup.isPending ? 'Criando conta...' : 'Criar conta'}
            </Button>
          </form>

          <p className="text-muted-foreground text-center text-xs">
            Ja tem conta?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Entrar
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
